import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  GripVertical,
  Eye,
  EyeOff,
  Palette,
  Tag,
  FolderPlus,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import { cn } from '../utils/cn';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';

export interface Category {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface CategoryManagerProps {
  onClose: () => void;
  onCategorySelect?: (category: Category) => void;
  isModal?: boolean;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({ 
  onClose, 
  onCategorySelect,
  isModal = true 
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const formRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#667eea',
    icon: '',
    sortOrder: 0,
    parentCategoryId: '' as string | undefined
  });

  const predefinedColors = [
    '#667eea', '#764ba2', '#f093fb', '#f5576c',
    '#4facfe', '#00f2fe', '#43e97b', '#38f9d7',
    '#ffecd2', '#fcb69f', '#a8edea', '#fed6e3',
    '#d299c2', '#fef9d7', '#667eea', '#764ba2'
  ];

  const predefinedIcons = [
    '📚', '🔧', '⚙️', '🎯', '🚀', '💡', '🔍', '📊',
    '🎨', '💻', '📱', '🌐', '🔒', '📈', '🎓', '⭐'
  ];

  useEffect(() => {
    loadCategories();
  }, []);

  // Auto-scroll to form when creating or editing
  useEffect(() => {
    if ((isCreating || editingCategory) && formRef.current) {
      // Small delay to ensure form is rendered
      setTimeout(() => {
        formRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'end',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [isCreating, editingCategory]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCategories();
      
      if (response.success && response.data) {
        setCategories(response.data as Category[]);
      } else {
        toast.error('Failed to load categories');
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = (parentId?: string) => {
    setIsCreating(true);
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      color: '#667eea',
      icon: '',
      sortOrder: categories.length,
      parentCategoryId: parentId || ''
    });
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setIsCreating(false);
    setFormData({
      name: category.name,
      description: category.description || '',
      color: category.color || '#667eea',
      icon: category.icon || '',
      sortOrder: category.sortOrder,
      parentCategoryId: category.parentCategoryId || ''
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      let response;
      if (editingCategory) {
        // Support both isActive (old) and isVisible (hierarchy) fields
        const currentState = (editingCategory as any).isVisible !== undefined 
          ? (editingCategory as any).isVisible 
          : editingCategory.isActive;
        
        response = await apiService.updateCategory(editingCategory.id, {
          name: formData.name,
          description: formData.description,
          color: formData.color,
          icon: formData.icon,
          sortOrder: formData.sortOrder,
          parentCategoryId: formData.parentCategoryId || undefined,
          isVisible: currentState !== false,
          isActive: currentState !== false // Support both for backward compatibility
        });
      } else {
        response = await apiService.createCategory({
          name: formData.name,
          description: formData.description,
          color: formData.color,
          icon: formData.icon,
          sortOrder: formData.sortOrder,
          parentCategoryId: formData.parentCategoryId || undefined
        });
      }
      
      if (response.success) {
        toast.success(editingCategory ? 'Category updated' : 'Category created');
        await loadCategories();
        setEditingCategory(null);
        setIsCreating(false);
        setFormData({
          name: '',
          description: '',
          color: '#667eea',
          icon: '',
          sortOrder: 0
        });
      } else {
        toast.error(response.error || 'Failed to save category');
      }
    } catch (error: any) {
      console.error('Error saving category:', error);
      toast.error(error.message || 'Failed to save category');
    }
  };

  const handleDelete = async (category: Category) => {
    if (!confirm(`Are you sure you want to delete "${category.name}"?`)) {
      return;
    }

    try {
      await apiService.deleteCategory(category.id);
      toast.success('Category deleted');
      await loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const handleToggleActive = async (category: Category) => {
    try {
      // Support both isActive (old) and isVisible (hierarchy) fields
      const currentState = (category as any).isVisible !== undefined 
        ? (category as any).isVisible 
        : category.isActive;
      const newState = !currentState;
      
      const response = await apiService.updateCategory(category.id, { 
        isVisible: newState,
        isActive: newState // Support both for backward compatibility
      });
      
      if (response.success) {
        toast.success(`Category ${newState ? 'activated' : 'deactivated'}`);
        await loadCategories();
      } else {
        toast.error(response.error || 'Failed to update category');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    }
  };

  const handleSelect = (category: Category) => {
    if (onCategorySelect) {
      onCategorySelect(category);
      onClose();
    }
  };

  const content = (
    <div className="space-y-6">
      {/* Header - only shown when not in modal mode */}
      {!isModal && (
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Manage Categories</h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => handleCreate()}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Category</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 bg-gray-200 rounded-full mx-auto mb-4 animate-pulse" />
            <p className="text-gray-500">Loading categories...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Tag className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No categories yet</p>
            <p className="text-sm">Create your first category to get started</p>
          </div>
        ) : (
          <div className="space-y-2">
            {(() => {
              // Build hierarchy tree with visibility control
              const buildTree = (parentId: string | null = null, level: number = 0, showAll: boolean = false): Array<Category & { _level: number }> => {
                return categories
                  .filter(cat => (cat.parentCategoryId || null) === parentId)
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .flatMap(cat => {
                    const catWithLevel = { ...cat, _level: level } as Category & { _level: number };
                    const isExpanded = showAll || expandedCategories.has(cat.id);
                    const children = isExpanded ? buildTree(cat.id, level + 1, showAll) : [];
                    return [catWithLevel, ...children];
                  });
              };
              
              const treeCategories = buildTree();
              
              return treeCategories.map((category) => {
                const level = category._level || 0;
                const hasChildren = categories.some(cat => cat.parentCategoryId === category.id);
                const isExpanded = expandedCategories.has(category.id);
                
                return (
                  <motion.div
                    key={category.id}
                    className={cn(
                      "p-4 rounded-lg border transition-all",
                      ((category as any).isVisible !== undefined ? (category as any).isVisible : category.isActive)
                        ? "bg-white border-gray-200 hover:border-gray-300" 
                        : "bg-gray-50 border-gray-100"
                    )}
                    style={{ marginLeft: `${level * 24}px` }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        {hasChildren ? (
                          <button
                            onClick={() => {
                              const newExpanded = new Set(expandedCategories);
                              if (newExpanded.has(category.id)) {
                                newExpanded.delete(category.id);
                              } else {
                                newExpanded.add(category.id);
                              }
                              setExpandedCategories(newExpanded);
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-600" />
                            )}
                          </button>
                        ) : (
                          <span className="w-6" />
                        )}
                        <GripVertical className="w-4 h-4 text-gray-400" />
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-medium"
                          style={{ backgroundColor: category.color }}
                        >
                          {category.icon || '📂'}
                        </div>
                      </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h3 className={cn(
                        "font-medium truncate",
                        ((category as any).isVisible !== undefined ? (category as any).isVisible : category.isActive) 
                          ? "text-gray-900" 
                          : "text-gray-500"
                      )}>
                        {category.name}
                      </h3>
                      {!((category as any).isVisible !== undefined ? (category as any).isVisible : category.isActive) && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
                          Inactive
                        </span>
                      )}
                    </div>
                    {category.description && (
                      <p className="text-sm text-gray-500 truncate mt-1">
                        {category.description}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleToggleActive(category)}
                      className={cn(
                        "p-2 rounded-lg transition-colors",
                        ((category as any).isVisible !== undefined ? (category as any).isVisible : category.isActive)
                          ? "text-green-600 hover:bg-green-50" 
                          : "text-gray-400 hover:bg-gray-100"
                      )}
                      title={((category as any).isVisible !== undefined ? (category as any).isVisible : category.isActive) ? "Deactivate" : "Activate"}
                    >
                      {((category as any).isVisible !== undefined ? (category as any).isVisible : category.isActive) ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => handleCreate(category.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Add subcategory"
                    >
                      <FolderPlus className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => handleDelete(category)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    
                    {onCategorySelect && (
                      <button
                        onClick={() => handleSelect(category)}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                      >
                        Select
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
                );
              });
            })()}
          </div>
        )}
      </div>

      {/* Create/Edit Form */}
      <AnimatePresence>
        {(isCreating || editingCategory) && (
          <motion.div
            ref={formRef}
            className="p-6 bg-gray-50 rounded-lg border border-gray-200"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingCategory ? 'Edit Category' : 'Create New Category'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Category name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Optional description"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parent Category (optional)
                </label>
                <select
                  value={formData.parentCategoryId}
                  onChange={(e) => setFormData(prev => ({ ...prev, parentCategoryId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Top-level category</option>
                  {categories
                    .filter(cat => !editingCategory || cat.id !== editingCategory.id) // Don't allow selecting self as parent
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select a parent to create a nested category
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <div className="flex flex-wrap gap-1">
                    {predefinedColors.map((color) => (
                      <button
                        key={color}
                        onClick={() => setFormData(prev => ({ ...prev, color }))}
                        className={cn(
                          "w-6 h-6 rounded border-2 transition-all",
                          formData.color === color ? "border-gray-400 scale-110" : "border-gray-200"
                        )}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon
                </label>
                <div className="flex flex-wrap gap-2">
                  {predefinedIcons.map((icon) => (
                    <button
                      key={icon}
                      onClick={() => setFormData(prev => ({ ...prev, icon }))}
                      className={cn(
                        "w-8 h-8 rounded border-2 flex items-center justify-center text-lg transition-all",
                        formData.icon === icon ? "border-blue-400 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                      )}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setEditingCategory(null);
                  setIsCreating(false);
                  setFormData({
                    name: '',
                    description: '',
                    color: '#667eea',
                    icon: '',
                    sortOrder: 0,
                    parentCategoryId: ''
                  });
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                <span>{editingCategory ? 'Update' : 'Create'}</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
        <motion.div
          className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto"
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        >
          <div className="sticky top-0 bg-white border-b border-gray-200 z-10 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Manage Categories</h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => handleCreate()}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add Category</span>
              </button>
            </div>
          </div>
          <div className="p-6">
            {content}
          </div>
        </motion.div>
      </div>
    );
  }

  return content;
};

export default CategoryManager;
